import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import interestFormConverter from "@/components/StudyCard/utils/interestFormConverter";
import { getFirestoreDB } from "@/firebase";
import { useFirebaseAuth } from "@/contexts/UserProfileContext/UserProfileContext";

type StudyRecommendation = {
    nctId: string;
    title: string;
    searchId: string;
    uid: string;
    runId: string;
};

type StudiesHooksContextValue = {
    uid?: string;
    savedStudies: string[];
    savedStudiesLoading: boolean;
    enrolledStudies: string[];
    enrolledStudiesLoading: boolean;
    recommendedStudies: StudyRecommendation[];
    recommendedStudiesLoading: boolean;
    ensureSavedStudies: () => void;
    ensureEnrolledStudies: () => void;
    ensureRecommendedStudies: () => void;
};

const StudiesHooksContext = createContext<StudiesHooksContextValue | null>(null);

function noop() {}

export function StudiesHooksProvider({ children }: { children: React.ReactNode }) {
    const { user, uid: cachedUid } = useFirebaseAuth();
    const uid = user?.uid || cachedUid;

    const [savedStudies, setSavedStudies] = useState<string[]>([]);
    const [savedStudiesLoading, setSavedStudiesLoading] = useState(false);
    const savedUnsubscribeRef = useRef<(() => void) | null>(null);

    const [enrolledStudies, setEnrolledStudies] = useState<string[]>([]);
    const [enrolledStudiesLoading, setEnrolledStudiesLoading] = useState(false);
    const enrolledUnsubscribeRef = useRef<(() => void) | null>(null);

    const [recommendedStudies, setRecommendedStudies] = useState<StudyRecommendation[]>([]);
    const [recommendedStudiesLoading, setRecommendedStudiesLoading] = useState(false);
    const recommendedUnsubscribeRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        savedUnsubscribeRef.current?.();
        savedUnsubscribeRef.current = null;
        setSavedStudies([]);
        setSavedStudiesLoading(false);

        enrolledUnsubscribeRef.current?.();
        enrolledUnsubscribeRef.current = null;
        setEnrolledStudies([]);
        setEnrolledStudiesLoading(false);

        recommendedUnsubscribeRef.current?.();
        recommendedUnsubscribeRef.current = null;
        setRecommendedStudies([]);
        setRecommendedStudiesLoading(false);
    }, [uid]);

    const ensureSavedStudies = useCallback(() => {
        if (!uid || savedUnsubscribeRef.current) return;

        setSavedStudiesLoading(true);
        const db = getFirestoreDB();
        const savedStudiesRef = collection(db, "userProfile", uid, "savedStudies").withConverter(interestFormConverter);
        savedUnsubscribeRef.current = onSnapshot(
            savedStudiesRef,
            (snapshot) => {
                const savedStudiesData = snapshot.docs.map((doc) => doc.data().nctID);
                setSavedStudies(savedStudiesData);
                setSavedStudiesLoading(false);
            },
            () => {
                setSavedStudies([]);
                setSavedStudiesLoading(false);
            }
        );
    }, [uid]);

    const ensureEnrolledStudies = useCallback(() => {
        if (!uid || enrolledUnsubscribeRef.current) return;

        setEnrolledStudiesLoading(true);
        const db = getFirestoreDB();
        const enrolledStudiesRef = collection(db, "userProfile", uid, "enrolledStudies");
        enrolledUnsubscribeRef.current = onSnapshot(
            enrolledStudiesRef,
            (snapshot) => {
                const enrolledStudiesData = snapshot.docs.map((doc) => {
                    const data = doc.data() as { nctID?: string; nctId?: string };
                    return data.nctID || data.nctId || doc.id;
                });
                setEnrolledStudies(enrolledStudiesData);
                setEnrolledStudiesLoading(false);
            },
            () => {
                setEnrolledStudies([]);
                setEnrolledStudiesLoading(false);
            }
        );
    }, [uid]);

    const ensureRecommendedStudies = useCallback(() => {
        if (!uid || recommendedUnsubscribeRef.current) return;

        setRecommendedStudiesLoading(true);
        const db = getFirestoreDB();
        const recommendedStudiesRef = collection(db, "userProfile", uid, "trialFinderRecommendations");
        const q = query(recommendedStudiesRef, where("dismissed", "!=", true));

        recommendedUnsubscribeRef.current = onSnapshot(
            q,
            (snapshot) => {
                const recommendations: StudyRecommendation[] = snapshot.docs.map((doc) => ({
                    nctId: doc.data().nctId,
                    title: doc.data().title,
                    searchId: doc.data().searchId,
                    uid: doc.data().uid,
                    runId: doc.data().runId,
                }));
                setRecommendedStudies(recommendations);
                setRecommendedStudiesLoading(false);
            },
            () => {
                setRecommendedStudies([]);
                setRecommendedStudiesLoading(false);
            }
        );
    }, [uid]);

    const value = useMemo<StudiesHooksContextValue>(
        () => ({
            uid,
            savedStudies,
            savedStudiesLoading,
            enrolledStudies,
            enrolledStudiesLoading,
            recommendedStudies,
            recommendedStudiesLoading,
            ensureSavedStudies: uid ? ensureSavedStudies : noop,
            ensureEnrolledStudies: uid ? ensureEnrolledStudies : noop,
            ensureRecommendedStudies: uid ? ensureRecommendedStudies : noop,
        }),
        [
            uid,
            savedStudies,
            savedStudiesLoading,
            enrolledStudies,
            enrolledStudiesLoading,
            recommendedStudies,
            recommendedStudiesLoading,
            ensureSavedStudies,
            ensureEnrolledStudies,
            ensureRecommendedStudies,
        ]
    );

    return <StudiesHooksContext.Provider value={value}>{children}</StudiesHooksContext.Provider>;
}

export function useStudiesHooksContextOptional(): StudiesHooksContextValue | null {
    return useContext(StudiesHooksContext);
}
